import React, { useState } from 'react';

export default function Landing({ onEnterWorkspace }) {
  const [demoInput, setDemoInput] = useState('');
  const [demoEmail, setDemoEmail] = useState('');
  const [isPrimaryHovered, setIsPrimaryHovered] = useState(false);

  const handleTryFree = () => {
    onEnterWorkspace();
  };

  const featureCards = [
    {
      icon: '◈',
      title: 'Industry-Specific AI',
      desc: 'Tailored messaging for fintech, healthcare, SaaS, freelancers, and ecommerce teams.'
    },
    {
      icon: '⚡',
      title: '5-Second Email Generation',
      desc: 'Turn a raw brief into a polished reply-focused email almost instantly.'
    },
    {
      icon: '📈',
      title: 'Reply Optimization',
      desc: 'Every draft is tuned to preempt objections and improve engagement quality.'
    },
    {
      icon: '✦',
      title: 'Smart Personalization',
      desc: 'Warm, premium language that feels human and strategic without losing clarity.'
    }
  ];

  const steps = [
    'Paste your customer brief or job description',
    'Select the industry and positioning cues',
    'Generate a polished reply-focused email in seconds'
  ];

  const testimonials = [
    '“Nyxaveo helped us turn generic outreach into sharper, more human messages instantly.”',
    '“The tone feels premium and the reply framing is noticeably better than our previous workflow.”',
    '“We now create campaign-ready copy faster, with less editing and better consistency.”'
  ];

  const pricingTiers = [
    {
      plan: 'Free Plan',
      price: '₹0',
      blurb: 'A clean starting point to test the workflow and create a few high-quality outreach drafts.',
      features: ['5 emails/month', 'General industry guidance', 'Basic reply insights'],
      featured: false
    },
    {
      plan: 'Pro Plan',
      price: '₹799/month',
      blurb: 'Built for growing teams that want polished copy, deeper targeting, and faster turnaround.',
      features: ['Unlimited emails', 'All core industries', 'Advanced reply scoring', 'Priority support'],
      featured: true
    },
    {
      plan: 'Agency Plan',
      price: '₹1999/month',
      blurb: 'For operators and agencies scaling multiple niches with custom workflow control and collaboration.',
      features: ['Unlimited seats', 'Custom industry modes', 'White-label setup', 'Dedicated success lead'],
      featured: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#fdfbf7] text-gray-950 font-sans antialiased">
      <div className="mx-auto flex max-w-7xl flex-col px-5 pb-20 pt-6 sm:px-8 lg:px-12">
        <div className="flex items-center gap-2">
          <img 
            src="/logo2.jpg" 
            alt="Logo" 
            className="h-8 w-8 rounded-full object-cover inline-block align-middle"
            onError={(e) => {
              // If local logo2.jpg is missing in public folder, load this beautiful premium geometric fallback icon instantly
              e.target.src = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=100&auto=format&fit=crop&q=80";
            }}
          />
          <span className="text-xl font-bold text-gray-950 inline-block align-middle">Nyxaveo</span>
        </div>

        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.28em] text-gray-700">
              Reply-Focused Sales Engine
            </p>
            <h1 className="max-w-2xl text-4xl font-black tracking-[-0.06em] text-gray-950 sm:text-[2.8rem] lg:text-[3.2rem]">
              Turn Raw Requirements Into High-Converting Sales Emails in 5 Seconds
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-gray-700">
              A clean milk-white workspace for building reply-focused cold emails with industry-aware AI and premium sales framing.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleTryFree}
                onMouseEnter={() => setIsPrimaryHovered(true)}
                onMouseLeave={() => setIsPrimaryHovered(false)}
                className={`rounded-full px-6 py-3 text-sm font-bold uppercase tracking-[0.12em] transition-all duration-200 ${
                  isPrimaryHovered
                    ? 'bg-gray-950 text-white shadow-[0_12px_30px_rgba(15,23,42,0.16)]'
                    : 'bg-gray-950 text-white shadow-[0_10px_24px_rgba(15,23,42,0.12)]'
                }`}
              >
                Try Free
              </button>

              <button
                type="button"
                onClick={handleTryFree}
                className="rounded-full border border-gray-200 bg-white px-6 py-3 text-sm font-bold uppercase tracking-[0.12em] text-gray-950 transition-all duration-200 hover:border-gray-300"
              >
                Generate Email
              </button>
            </div>
          </div>

          <div className="rounded-[28px] border border-gray-200/60 bg-white p-4 shadow-[0_12px_40px_rgba(15,23,42,0.06)] sm:p-5">
            <div className="rounded-[24px] border border-gray-200/80 bg-[#fcfbf8] p-4 sm:p-5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-gray-700">Live Demo</p>
                  <p className="mt-2 text-sm text-gray-600">Preview a polished email draft in a clean light workspace.</p>
                </div>
                <span className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-gray-900">
                  Draft
                </span>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <div className="rounded-[20px] border border-gray-200 bg-white p-4">
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-gray-700">
                    Input Brief
                  </label>
                  <textarea
                    value={demoInput}
                    onChange={(e) => setDemoInput(e.target.value)}
                    placeholder="Paste a client brief, job description, or outreach note…"
                    className="min-h-[260px] w-full rounded-[16px] border border-gray-200 bg-white px-4 py-3 text-sm leading-7 text-gray-950 outline-none resize-none"
                  />
                </div>

                <div className="rounded-[20px] border border-gray-200 bg-white p-4">
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-gray-700">
                    Generated Email
                  </label>
                  <pre className="min-h-[260px] rounded-[16px] border border-gray-200 bg-[#fcfbf8] px-4 py-4 text-sm leading-7 text-gray-800 whitespace-pre-wrap">
                    {demoEmail || 'Your generated reply-focused email will appear here…'}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-14">
          <div className="mb-6 text-center">
            <p className="text-sm font-bold uppercase tracking-[0.28em] text-gray-700">Features</p>
            <h2 className="mt-3 text-3xl font-black tracking-[-0.05em] text-gray-950 sm:text-[2.2rem]">
              Premium intelligence for reply-focused sales teams
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {featureCards.map((item) => (
              <div
                key={item.title}
                className="rounded-[24px] border border-gray-200/60 bg-white px-5 py-6 shadow-[0_8px_30px_rgba(15,23,42,0.04)]"
              >
                <div className="mb-3 text-2xl text-gray-900">{item.icon}</div>
                <h3 className="text-lg font-black text-gray-950">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-gray-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <div className="mb-6 text-center">
            <p className="text-sm font-bold uppercase tracking-[0.28em] text-gray-700">How it works</p>
            <h2 className="mt-3 text-3xl font-black tracking-[-0.05em] text-gray-950 sm:text-[2.2rem]">
              Three clean steps to better replies
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {steps.map((step, index) => (
              <div key={step} className="rounded-[24px] border border-gray-200/60 bg-white px-5 py-6">
                <div className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-gray-700">0{index + 1}</div>
                <p className="text-lg font-bold text-gray-950">{step}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <div className="mb-6 text-center">
            <p className="text-sm font-bold uppercase tracking-[0.28em] text-gray-700">Testimonials</p>
            <h2 className="mt-3 text-3xl font-black tracking-[-0.05em] text-gray-950 sm:text-[2.2rem]">
              Trusted by teams who care about conversion quality
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {testimonials.map((quote) => (
              <div key={quote} className="rounded-[24px] border border-gray-200/60 bg-white px-5 py-6 text-sm leading-7 text-gray-700">
                {quote}
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <div className="mb-6 text-center">
            <p className="text-sm font-bold uppercase tracking-[0.28em] text-gray-700">Pricing</p>
            <h2 className="mt-3 text-3xl font-black tracking-[-0.05em] text-gray-950 sm:text-[2.2rem]">
              Choose the plan that fits your outreach motion
            </h2>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {pricingTiers.map((tier) => (
              <div
                key={tier.plan}
                className={`rounded-[26px] border px-5 py-6 ${
                  tier.featured
                    ? 'border-gray-300 bg-[#fcfbf8] shadow-[0_12px_32px_rgba(15,23,42,0.08)]'
                    : 'border-gray-200/60 bg-white'
                }`}
              >
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-gray-700">{tier.plan}</p>
                <p className="mt-4 text-3xl font-black text-gray-950">{tier.price}</p>
                <p className="mt-3 text-sm leading-7 text-gray-700">{tier.blurb}</p>
                <ul className="mt-5 space-y-3 text-sm text-gray-800">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <span className="text-gray-500">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={handleTryFree}
                  className={`mt-6 w-full rounded-full px-4 py-3 text-sm font-bold uppercase tracking-[0.12em] transition-all duration-200 ${
                    tier.featured
                      ? 'bg-gray-950 text-white'
                      : 'border border-gray-200 bg-white text-gray-950'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
